import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Navigation, Search, Globe, Map, X, Loader2 } from 'lucide-react';
import { isPlausibleGeolocation } from '../utils/geo';
import LocationPickerMap from './LocationPickerMap';
import styles from './CityPickerModal.module.css';

/**
 * Modern Consumer Location Picker Modal
 */
export default function CityPickerModal({
  isOpen,
  cities = [],
  citiesLoading,
  allowDismiss = true,
  onDismiss,
  onConfirmCity,
  onBrowseAll,
  onUseLiveLocation,
}) {
  const [activeTab, setActiveTab] = useState('cities'); // 'cities' | 'map'
  const [citySearch, setCitySearch] = useState('');
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setLocating(false);
      setLocError('');
      setCitySearch('');
      setActiveTab('cities');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && allowDismiss) {
        onDismiss?.();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, allowDismiss, onDismiss]);

  const sortedCities = useMemo(() => {
    return (cities || [])
      .filter((c) => c && c.active !== false && c.cityName)
      .sort((a, b) => String(a.cityName).localeCompare(String(b.cityName)));
  }, [cities]);

  const filteredCities = useMemo(() => {
    const q = citySearch.trim().toLowerCase();
    if (!q) return sortedCities;
    return sortedCities.filter((c) => String(c.cityName).toLowerCase().includes(q));
  }, [sortedCities, citySearch]);

  const mapBias = useMemo(() => {
    const c = (cities || []).find(
      (x) => x?.location?.latitude != null && x?.location?.longitude != null
    );
    if (c?.location) {
      return {
        lat: c.location.latitude,
        lng: c.location.longitude,
      };
    }
    return { lat: undefined, lng: undefined };
  }, [cities]);

  const applyMapPayload = useCallback(
    ({ lat, lng, cityHintFromUrl, cityNameFromPhoton }) => {
      const name = (cityHintFromUrl || cityNameFromPhoton || '').trim();
      if (name && cities.length) {
        const m = cities.find(
          (c) => String(c.cityName).trim().toLowerCase() === name.toLowerCase()
        );
        if (m) {
          onConfirmCity(m);
          onDismiss?.();
          return;
        }
      }
      onUseLiveLocation(lat, lng, name || undefined);
      onDismiss?.();
    },
    [cities, onConfirmCity, onUseLiveLocation, onDismiss]
  );

  const handleSelectCity = (city) => {
    onConfirmCity(city);
    onDismiss?.();
  };

  const handleBrowseAll = () => {
    onBrowseAll();
    onDismiss?.();
  };

  const handleLiveLocation = () => {
    setLocError('');
    if (typeof onUseLiveLocation !== 'function') return;
    if (!navigator.geolocation) {
      setLocError('Location is not supported in this browser.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        if (!isPlausibleGeolocation(pos.coords)) {
          setLocError('Could not get a valid position. Please choose a city below.');
          return;
        }
        onUseLiveLocation(pos.coords.latitude, pos.coords.longitude);
        onDismiss?.();
      },
      (err) => {
        setLocating(false);
        if (err?.code === 1) {
          setLocError('Location permission denied. Please select your city below.');
        } else if (err?.code === 2) {
          setLocError('Position unavailable. Please select your city below.');
        } else {
          setLocError('Could not fetch GPS location. Please select your city below.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onClick={allowDismiss ? onDismiss : undefined}
    >
      <div
        className={styles.modalPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIconBox}>
              <MapPin size={20} />
            </div>
            <div>
              <h2 id="location-modal-title" className={styles.modalTitle}>
                {allowDismiss ? 'Choose Location' : 'Select Your City'}
              </h2>
              <p className={styles.modalSubtitle}>
                Discover salons, master stylists, and pricing near you
              </p>
            </div>
          </div>
          {allowDismiss && (
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onDismiss}
              aria-label="Close location modal"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* 1-Click GPS Button */}
          <div className={styles.liveLocationCard}>
            <div className={styles.liveInfo}>
              <Navigation size={18} className={styles.liveGpsIcon} />
              <div>
                <div className={styles.liveTitle}>Use Current Location</div>
                <div className={styles.liveDesc}>Detect your area automatically via GPS</div>
              </div>
            </div>
            <button
              type="button"
              className={styles.liveBtn}
              onClick={handleLiveLocation}
              disabled={locating}
            >
              {locating ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Locating…</span>
                </>
              ) : (
                <span>Locate Me</span>
              )}
            </button>
          </div>

          {locError && <div className={styles.locError}>{locError}</div>}

          {/* Segmented Tab Switcher */}
          <div className={styles.tabsRow}>
            <button
              type="button"
              className={`${styles.tabBtn} ${activeTab === 'cities' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('cities')}
            >
              <Globe size={14} />
              <span>Select City</span>
            </button>
            <button
              type="button"
              className={`${styles.tabBtn} ${activeTab === 'map' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('map')}
            >
              <Map size={14} />
              <span>Map &amp; Pin</span>
            </button>
          </div>

          {/* Tab 1: City Search & Fast Chips */}
          {activeTab === 'cities' && (
            <div className={styles.citySection}>
              <div className={styles.citySearchWrap}>
                <Search size={15} style={{ color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Search your city (e.g. Noida, Delhi, Mumbai)…"
                  className={styles.citySearchInput}
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                />
              </div>

              {citiesLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  <Loader2
                    size={24}
                    style={{
                      animation: 'spin 1s linear infinite',
                      color: '#dc2626',
                      margin: '0 auto 8px',
                    }}
                  />
                  <p style={{ fontSize: '0.85rem' }}>Loading active cities…</p>
                </div>
              ) : filteredCities.length > 0 ? (
                <div>
                  <div className={styles.sectionLabel}>
                    {citySearch ? 'Matching Cities' : 'Available Cities'}
                  </div>
                  <div className={styles.cityChipsGrid}>
                    {filteredCities.map((c) => (
                      <button
                        key={c._id}
                        type="button"
                        className={styles.cityChip}
                        onClick={() => handleSelectCity(c)}
                      >
                        <MapPin size={13} style={{ color: '#dc2626', flexShrink: 0 }} />
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.cityName}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '1.5rem',
                    color: '#64748b',
                    fontSize: '0.85rem',
                  }}
                >
                  No cities match &ldquo;{citySearch}&rdquo;. You can use the map tab or browse all
                  areas.
                </div>
              )}

              <button type="button" className={styles.btnAllAreas} onClick={handleBrowseAll}>
                Browse All Areas / Nationwide
              </button>
            </div>
          )}

          {/* Tab 2: Map & Pin */}
          {activeTab === 'map' && (
            <div>
              <LocationPickerMap
                onChosen={applyMapPayload}
                biasLat={mapBias.lat}
                biasLng={mapBias.lng}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
